import cn from 'classnames'
import React, {
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { useTable, TableOptions, Row } from 'react-table'

import LoadingIndicator from '@/common/components/LoadingIndicator'
import ArrowDownIcon from '@/common/components/icons/ArrowDownIcon'
import useI18n from '@/common/hooks/useI18n'
import { NO_DATA } from '@/common/translation'

import {
  StyledTable,
  StyledRow,
  StyledHeaderCell,
  StyledCell,
  Expander,
  ExpendedContent,
} from './styled'

const EXPANDER_COLUMN = {
  id: '_expander',
  Header: () => null,
  Cell: ({ row, expanded }) => (
    <Expander $expanded={!!expanded[row.index]}>
      <ArrowDownIcon size={10} />
    </Expander>
  ),
}

export interface TableProps<T extends object = { [key: string]: any }>
  extends TableOptions<T> {
  isLoading?: boolean
  shouldRenderExpandedComponent?: boolean
  expandedContentMaxHeight?: number
  getRowProps?: (props: { row: Row<T> }) => HTMLAttributes<HTMLTableRowElement>
  renderExpandedComponent?: (props: { row: Row<T> }) => ReactNode
}

const Table = ({
  columns: _column,
  isLoading,
  getRowProps,
  renderExpandedComponent,
  shouldRenderExpandedComponent = true,
  expandedContentMaxHeight = 1000,
  ...props
}: TableProps) => {
  const { t } = useI18n()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const expandable = renderExpandedComponent && shouldRenderExpandedComponent
  const columns = useMemo(
    () => (expandable ? [EXPANDER_COLUMN, ..._column] : _column),
    [_column, expandable]
  )

  const {
    rows,
    headerGroups,
    visibleColumns,
    getTableProps,
    getTableBodyProps,
    prepareRow,
  } = useTable({ columns, ...props })

  useEffect(() => {
    if (!isLoading) return
    setExpanded({})
  }, [isLoading])

  return (
    <StyledTable {...getTableProps()}>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <StyledHeaderCell
                {...column.getHeaderProps({ style: (column as any).style })}
              >
                {column.render('Header')}
              </StyledHeaderCell>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {isLoading || !rows.length ? (
          <StyledRow>
            <td colSpan={visibleColumns.length}>
              <div className='flex align-center justify-center text-14 py-56 px-12'>
                {isLoading ? <LoadingIndicator /> : t(NO_DATA)}
              </div>
            </td>
          </StyledRow>
        ) : (
          rows.map(row => {
            prepareRow(row)
            const { key, ...rowProps } = row.getRowProps()
            const customRowProps = getRowProps?.({ row })

            const onClickRow = (event: MouseEvent<HTMLTableRowElement>) => {
              if (expandable) {
                setExpanded(state => ({
                  ...state,
                  [row.index]: !state[row.index],
                }))
              }
              customRowProps?.onClick?.(event)
            }

            return (
              <React.Fragment key={key}>
                <StyledRow
                  {...rowProps}
                  {...customRowProps}
                  className={cn(
                    expandable && 'clickable',
                    rowProps?.className,
                    customRowProps?.className
                  )}
                  onClick={onClickRow}
                >
                  {row.cells.map(cell => (
                    <StyledCell {...cell.getCellProps()}>
                      {cell.render('Cell', { expanded })}
                    </StyledCell>
                  ))}
                </StyledRow>
                {expandable ? (
                  <tr>
                    <td />
                    <td colSpan={visibleColumns.length - 1}>
                      <ExpendedContent
                        $expanded={!!expanded[row.index]}
                        $maxHeight={expandedContentMaxHeight}
                      >
                        <div className='pl-6 pb-12'>
                          {renderExpandedComponent({ row })}
                        </div>
                      </ExpendedContent>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            )
          })
        )}
      </tbody>
    </StyledTable>
  )
}

export default Table
